import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { PropertyProvider } from "@/contexts/PropertyContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import RegisterHotel from "@/pages/RegisterHotel";
import RegisterTraveller from "@/pages/RegisterTraveller";
import Onboarding from "@/pages/Onboarding";
import PartnerCTA from "@/pages/PartnerCTA";
import HotelSearch from "@/pages/HotelSearch";
import HotelDetail from "@/pages/HotelDetail";

import CustomerDashboard from "@/pages/CustomerDashboard";
import MyNegotiations from "@/pages/MyNegotiations";
import MyBookings from "@/pages/MyBookings";
import BookingRequests from "@/pages/BookingRequests";
import BookingRequestDetail from "@/pages/BookingRequestDetail";
import RequestsBrowse from "@/pages/RequestsBrowse";
import ChatList from "@/pages/ChatList";
import ChatRoom from "@/pages/ChatRoom";

import PartnerDashboard from "@/pages/PartnerDashboard";
import PartnerProfile from "@/pages/PartnerProfile";
import PartnerRooms from "@/pages/PartnerRooms";
import PartnerInventory from "@/pages/PartnerInventory";
import PartnerLeads from "@/pages/PartnerLeads";
import PartnerReservations from "@/pages/PartnerReservations";
import AddProperty from "@/pages/AddProperty";
import RefundRequests from "@/pages/RefundRequests";
import StaffAccept from "@/pages/StaffAccept";

import AdminPanel from "@/pages/AdminPanel";

import MyTrips from "@/pages/MyTrips";
import MyWallet from "@/pages/MyWallet";
import Rewards from "@/pages/Rewards";
import Referrals from "@/pages/Referrals";
import Settings from "@/pages/Settings";
import SupportCenter from "@/pages/SupportCenter";
import NewTicket from "@/pages/NewTicket";
import MyTickets from "@/pages/MyTickets";
import TicketDetail from "@/pages/TicketDetail";
import AdminSupport from "@/pages/AdminSupport";
import BookingProtection from "@/pages/BookingProtection";
import Invoice from "@/pages/Invoice";
import PartnerAIStudio from "@/pages/PartnerAIStudio";
import PartnerAIDrafts from "@/pages/PartnerAIDrafts";
import TravellerConcierge from "@/components/TravellerConcierge";
import Footer from "@/components/Footer";
import MobileBottomNav from "@/components/MobileBottomNav";
import WhatsAppFab from "@/components/WhatsAppFab";
import { About, Contact, Terms, Privacy, DataProtection } from "@/pages/Legal";
import PayForBooking from "@/pages/PayForBooking";
import PartnerOnboarding from "@/pages/PartnerOnboarding";
import AiAssessment from "@/pages/AiAssessment";

function AppRoutes() {
  const location = useLocation();
  // Detect Emergent Auth callback synchronously (before any other route)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Standalone static HTML — served without navbar/footer/layout chrome.
  if (location.pathname === "/partner/ai-assessment") {
    return <AiAssessment />;
  }
  return (
    <>
      <Navbar />
      <ErrorBoundary route={location.pathname}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register/hotel" element={<RegisterHotel />} />
        <Route path="/register/traveller" element={<RegisterTraveller />} />
        <Route path="/register" element={<RegisterTraveller />} />
        <Route path="/travellers" element={<Login />} />
        <Route path="/hotels-portal" element={<Login />} />
        <Route path="/ops" element={<Login />} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/partner-cta" element={<PartnerCTA />} />
        <Route path="/search" element={<HotelSearch />} />
        <Route path="/hotels/:hotelId" element={<HotelDetail />} />

        <Route path="/customer" element={<ProtectedRoute roles={["customer"]}><CustomerDashboard /></ProtectedRoute>} />
        <Route path="/negotiations" element={<ProtectedRoute roles={["customer", "hotel_partner"]}><MyNegotiations /></ProtectedRoute>} />
        <Route path="/bookings" element={<ProtectedRoute roles={["customer", "hotel_partner"]}><MyBookings /></ProtectedRoute>} />
        <Route path="/requests" element={<ProtectedRoute roles={["customer"]}><BookingRequests /></ProtectedRoute>} />
        <Route path="/requests/browse" element={<RequestsBrowse />} />
        <Route path="/requests/:requestId" element={<ProtectedRoute><BookingRequestDetail /></ProtectedRoute>} />
        <Route path="/chats" element={<ProtectedRoute><ChatList /></ProtectedRoute>} />
        <Route path="/chats/:threadId" element={<ProtectedRoute><ChatRoom /></ProtectedRoute>} />

        <Route path="/partner" element={<ProtectedRoute roles={["hotel_partner"]}><PartnerDashboard /></ProtectedRoute>} />
        <Route path="/partner/profile" element={<ProtectedRoute roles={["hotel_partner"]}><PartnerProfile /></ProtectedRoute>} />
        <Route path="/partner/rooms" element={<ProtectedRoute roles={["hotel_partner"]}><PartnerRooms /></ProtectedRoute>} />
        <Route path="/partner/inventory" element={<ProtectedRoute roles={["hotel_partner"]}><PartnerInventory /></ProtectedRoute>} />
        <Route path="/partner/leads" element={<ProtectedRoute roles={["hotel_partner"]}><PartnerLeads /></ProtectedRoute>} />
        <Route path="/partner/reservations" element={<ProtectedRoute roles={["hotel_partner"]}><PartnerReservations /></ProtectedRoute>} />
        <Route path="/partner/add-property" element={<ProtectedRoute roles={["hotel_partner"]}><AddProperty /></ProtectedRoute>} />
        <Route path="/partner/refund-requests" element={<ProtectedRoute roles={["hotel_partner", "hotel_partner_staff"]}><RefundRequests /></ProtectedRoute>} />
        <Route path="/staff/accept/:token" element={<StaffAccept />} />
        <Route path="/partner/ai" element={<ProtectedRoute roles={["hotel_partner"]}><PartnerAIStudio /></ProtectedRoute>} />
        <Route path="/partner/ai-drafts" element={<ProtectedRoute roles={["hotel_partner"]}><PartnerAIDrafts /></ProtectedRoute>} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/data-protection" element={<DataProtection />} />
        <Route path="/pay/booking/:bookingId" element={<ProtectedRoute roles={["customer"]}><PayForBooking /></ProtectedRoute>} />
        <Route path="/partner/onboarding" element={<ProtectedRoute roles={["hotel_partner_pending", "hotel_partner"]}><PartnerOnboarding /></ProtectedRoute>} />

        <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminPanel /></ProtectedRoute>} />
        <Route path="/admin/support" element={<ProtectedRoute roles={["admin"]}><AdminSupport /></ProtectedRoute>} />

        {/* Phase 2 — loyalty, referrals, wallet, trips, support, invoice */}
        <Route path="/trips" element={<ProtectedRoute roles={["customer"]}><MyTrips /></ProtectedRoute>} />
        <Route path="/wallet" element={<ProtectedRoute roles={["customer"]}><MyWallet /></ProtectedRoute>} />
        <Route path="/rewards" element={<ProtectedRoute roles={["customer"]}><Rewards /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute roles={["customer"]}><Referrals /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/support" element={<SupportCenter />} />
        <Route path="/support/new" element={<ProtectedRoute><NewTicket /></ProtectedRoute>} />
        <Route path="/support/tickets" element={<ProtectedRoute><MyTickets /></ProtectedRoute>} />
        <Route path="/support/ticket/:ticketId" element={<ProtectedRoute><TicketDetail /></ProtectedRoute>} />
        <Route path="/booking-protection" element={<BookingProtection />} />
        <Route path="/invoice/:bookingId" element={<ProtectedRoute><Invoice /></ProtectedRoute>} />
      </Routes>
      </ErrorBoundary>
      <Footer />
      <TravellerConcierge />
      <MobileBottomNav />
      <WhatsAppFab />
      <Toaster richColors position="top-right" />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PropertyProvider>
            <AppRoutes />
          </PropertyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
